import CardWrapperStyles from "./CardWrapperStyles";
import SummaryMetricsRow, { SummaryMetricType } from "./SummaryMetricsRow";
import styled from "styled-components/macro";
import MetricsExplainer from "./MetricsExplainer/MetricsExplainer";
import { BREAKPOINT_MOBILE } from "../../constants/constants";
import {
  useGeAndLtFromUrl,
  useProcessGroupNameAndMachemetryIds,
} from "./reportUtils";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS } from "reports/utils/timeUtils";
import { getEosDateStringWithGeAndLt } from "./ReportHeader";
import { useTranslation } from "react-i18next";
import { MetricExplainerInputType } from "./MetricsExplainer/metricExplainerTypes";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useState } from "react";
import { TimeZoneController } from "components/Settings/TimeZoneController";
import { useReportConfigTimeZone } from "components/ReportsPage/reportPageUtils";
import { useDateMetadataDisplay } from "components/FlightTrends/DateRange/useDateMetadataDisplay";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { ShiftSegment } from "reports/types";
import { atom, useAtom } from "jotai";
import { AMERICA_NEW_YORK } from "utils/timeUtils";

export default function SummaryMetrics({
  summaryMetrics,
  processGroupName,
  customReportTitle,
  metricsExplainer = [],
  is24HourReport,
}: {
  summaryMetrics: SummaryMetricType[][];
  processGroupName?: string; // TODO MAKE MANDATORY
  customReportTitle?: string;
  metricsExplainer?: MetricExplainerInputType[];
  is24HourReport?: boolean;
}) {
  const customTitleFromUrl = new URLSearchParams(window.location.search).get(
    "customReportTitle"
  );
  const { reportHeader, dateString } = useEOSReportHeader({
    customReportTitle: customTitleFromUrl || customReportTitle,
    processGroupName,
    is24HourReport,
  });
  const timeZoneConfig = useReportConfigTimeZone();
  const timeZone = timeZoneConfig ?? AMERICA_NEW_YORK;
  const { timezoneDisplay } = useDateMetadataDisplay();
  const isAdminUser = useIsAdmin();

  return (
    <CardWrapperStyles>
      <ReportsSummaryMetricsCardControls />
      <div className="SummaryMetricsCard header">{reportHeader}</div>
      {timeZone && isAdminUser ? (
        <Tooltip title="(visible to Raven emails only)">
          <div className="headerTimeZone">{timeZone}</div>
        </Tooltip>
      ) : null}
      <div className="SummaryMetricsCard subHeader">
        {dateString}
        <TimeZoneIndicatorStyles>
          {timezoneDisplay ? `Viewing in ${timezoneDisplay}` : null}
        </TimeZoneIndicatorStyles>
      </div>
      <SummaryMetricStyles
        $numRows={summaryMetrics.filter(row => row.length).length}
      >
        {summaryMetrics
          .filter(row => row.length > 0)
          .map(summaryMetricsRow => (
            <div key={Math.random()} className="summaryMetrics">
              <SummaryMetricsRow metricsList={summaryMetricsRow} />
            </div>
          ))}
        {metricsExplainer.length !== 0 && (
          <div className="metricsExplainer">
            <MetricsExplainer metricsExplainer={metricsExplainer} />
          </div>
        )}
      </SummaryMetricStyles>
    </CardWrapperStyles>
  );
}
const TimeZoneIndicatorStyles = styled.div`
  transform-origin: top center;
  transform: scale(0.75);
`;

export const shiftsEosReportAtom = atom<ShiftSegment[]>([]);

/**
 * Gets EOS report header based on timerange, processGroup and shift
 * Overridable by customReportTitle
 */
export function useEOSReportHeader({
  customReportTitle,
  processGroupName,
  is24HourReport,
}: {
  customReportTitle?: string;
  processGroupName?: string;
  is24HourReport?: boolean;
}) {
  const [shiftsData] = useAtom(shiftsEosReportAtom);
  const { ge, lt } = useGeAndLtFromUrl();
  const { machemetryIds } = useProcessGroupNameAndMachemetryIds({
    defaultProcessGroupName: processGroupName,
  });
  // need to wait for machemetryIds to be fetched before we can fetch shifts
  const readyForShifts = machemetryIds.length > 0;
  const shiftDatum = shiftsData.find(shift => shift.range.ge === String(ge));
  const shiftName = shiftDatum?.shift?.name ?? "(...)";
  const dateString = getEosDateStringWithGeAndLt(ge, lt);
  const { i18n } = useTranslation("EOSReport");
  const isReportInFrench = i18n.language === "fr";

  // Some customers don't want the NotScheduled shifts to be included in the 24 hour report
  // hence the range of the 24 hour report is less than 24 hours
  // set is24HourReport to true for such reports
  const is24Hours = is24HourReport || (lt - ge) / ONE_HOUR_OF_MS === 24;
  const isWeekly = (lt - ge) / ONE_DAY_OF_MS > 3;

  // If shiftName is a number, format as e.g. Shift - 1
  // Else shifName is a string, format as e.g. Day Shift
  const shiftReportTitle = shiftName?.includes("Shift") // If it's a shift report and shift name already contains "Shift", don't add "Shift"
    ? shiftName
    : /^-?\d+$/.test(shiftName || "") // If shift name is a number only
      ? `Shift ${shiftName}` // prefix with Shift
      : `${shiftName} Shift`; // else suffix

  const reportTitle = customReportTitle
    ? customReportTitle
    : isWeekly
      ? "Weekly"
      : is24Hours
        ? "24 Hours"
        : isReportInFrench
          ? shiftName
          : shiftReportTitle;
  const reportConfig = useReportConfig();
  // If report is for a specific processGroup, pre-pend that to the front of the title
  const reportHeader =
    processGroupName && !reportConfig?.eosReportGeneralConfig?.customTitlePrefix
      ? `${processGroupName} - ${reportTitle}`
      : reportTitle;

  return {
    reportHeader,
    dateString,
    readyForShifts,
  };
}

const SummaryMetricStyles = styled.div<{ $numRows: number }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: repeat(${props => props.$numRows}, auto);
  grid-row-gap: 2em;
  justify-items: center;
  font-family: Roboto;

  .summaryMetrics {
    grid-column-start: 2;
  }

  .metricsExplainer {
    align-self: center;
    justify-self: flex-end !important;
    margin-right: 2em;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    grid-row-gap: 10px;

    .summaryMetrics {
      grid-column-start: 1;
    }

    .metricsExplainer {
      padding-top: 1em;
      justify-self: center !important;
      margin-right: 0;
    }
  }
`;

/** little gear icon in top-right that allows changing settings relevant to the card */
export function ReportsSummaryMetricsCardControls() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <ReportsConfigControlsStyles>
      <Tooltip title="Settings" placement="top">
        <IconButton onClick={handleClick} size="small">
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        disablePortal={true}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          onMouseLeave: handleClose,
        }}
        disableScrollLock={true}
      >
        <MenuItem disableRipple={true}>
          <TimeZoneController />
        </MenuItem>
      </Menu>
    </ReportsConfigControlsStyles>
  );
}

const ReportsConfigControlsStyles = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  @media print {
    display: none;
  }
  [data-testid="SettingsIcon"] {
    transform: scale(0.8);
  }
  [class*="TimeZoneController__Styles"] {
    gap: 8px;
  }
`;
